import { useEffect, useRef, useState } from "react";

import type { ProtectedVaultRecord } from "@safesphere/contracts";

import { PageIntro } from "../components/PageIntro.js";
import { Icon } from "../components/Icon.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { commitFamilyPlanImport, exportFamilyPlan, previewFamilyPlanImport } from "./backup.js";
import type { RecoveryPreview } from "./backup.js";
import {
  hasVault,
  initializeVault,
  isVaultUnlocked,
  loadProtectedRecords,
  loadQuickPlan,
  lockVault,
  saveQuickPlan,
  unlockVault,
} from "./familyRepository.js";
import type { QuickPlanData } from "./familyRepository.js";
import { createEmptyFamilyPlanSnapshot, fictionalPlanId } from "./fictionalFixtures.js";
import { ReunificationCardsPanel } from "./ReunificationCardsPanel.js";

type FamilyTab = "OVERVIEW" | "CONTACTS" | "MEETING" | "CHECKLISTS" | "VAULT" | "CARDS" | "BACKUP";

export function FamilyPreparednessPlanPage() {
  const { locale, t } = useI18n();
  const [tab, setTab] = useState<FamilyTab>("OVERVIEW");
  const [quick, setQuick] = useState<QuickPlanData>();
  const [vaultExists, setVaultExists] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [protectedRecords, setProtectedRecords] = useState<readonly ProtectedVaultRecord[]>([]);
  const [passphrase, setPassphrase] = useState("");
  const [vaultMessage, setVaultMessage] = useState("");
  const [backupText, setBackupText] = useState("");
  const [backupPassphrase, setBackupPassphrase] = useState("");
  const [preview, setPreview] = useState<RecoveryPreview>();
  const [backupMessage, setBackupMessage] = useState("");
  const sectionHeading = useRef<HTMLHeadingElement>(null);
  const hasSelectedTab = useRef(false);

  const refresh = async () => {
    const loaded = await loadQuickPlan(fictionalPlanId);
    setQuick(loaded);
    const exists = await hasVault(fictionalPlanId);
    setVaultExists(exists);
    const open = isVaultUnlocked(fictionalPlanId);
    setUnlocked(open);
    setProtectedRecords(open ? await loadProtectedRecords(fictionalPlanId) : []);
  };

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    if (!hasSelectedTab.current) {
      hasSelectedTab.current = true;
      return;
    }
    sectionHeading.current?.focus({ preventScroll: true });
  }, [tab]);

  const startPlan = async () => {
    await saveQuickPlan(
      createEmptyFamilyPlanSnapshot(locale === "th" ? "แผนของครอบครัวฉัน" : "My family plan"),
    );
    await refresh();
  };

  const setupVault = async () => {
    if (passphrase.length < 8) {
      setVaultMessage(t("family.vault.short"));
      return;
    }
    await initializeVault(fictionalPlanId, passphrase);
    setPassphrase("");
    setVaultMessage(t("family.vault.created"));
    await refresh();
  };

  const unlock = async () => {
    const result = await unlockVault(fictionalPlanId, passphrase);
    setPassphrase("");
    setVaultMessage(result.ok ? t("family.vault.unlocked") : t("family.vault.incorrect"));
    await refresh();
  };

  const lock = () => {
    lockVault(fictionalPlanId);
    setUnlocked(false);
    setProtectedRecords([]);
    setVaultMessage(t("family.vault.locked"));
  };

  const updateLabel = async (label: string) => {
    if (quick === undefined) return;
    const next = { ...quick, plan: { ...quick.plan, label, updatedAt: new Date().toISOString() } };
    setQuick(next);
    await saveQuickPlan(next);
  };

  const toggleChecklist = async (checklistId: string, itemId: string) => {
    if (quick === undefined) return;
    const next = {
      ...quick,
      checklists: quick.checklists.map((list) =>
        list.id === checklistId
          ? {
              ...list,
              items: list.items.map((item) =>
                item.id === itemId ? { ...item, completed: !item.completed } : item,
              ),
            }
          : list,
      ),
    };
    setQuick(next);
    await saveQuickPlan(next);
  };

  const updateContactName = async (id: string, name: string) => {
    if (quick === undefined) return;
    const next = {
      ...quick,
      contacts: quick.contacts.map((record) =>
        record.id === id ? { ...record, name, updatedAt: new Date().toISOString() } : record,
      ),
    };
    setQuick(next);
    await saveQuickPlan(next);
  };

  const updateMeetingLabel = async (id: string, label: string) => {
    if (quick === undefined) return;
    const next = {
      ...quick,
      meetingPoints: quick.meetingPoints.map((record) =>
        record.id === id ? { ...record, label, updatedAt: new Date().toISOString() } : record,
      ),
    };
    setQuick(next);
    await saveQuickPlan(next);
  };

  const exportBackup = async () => {
    try {
      setBackupText(await exportFamilyPlan(fictionalPlanId, backupPassphrase));
      setBackupMessage(t("family.backup.ready"));
    } catch {
      setBackupMessage(t("family.backup.unlock_required"));
    }
  };

  const inspectImport = async () => {
    const result = await previewFamilyPlanImport(backupText, backupPassphrase);
    if (!result.ok) {
      setPreview(undefined);
      setBackupMessage(t(`family.backup.${result.reason}`));
      return;
    }
    setPreview(result.preview);
    setBackupMessage(t("family.backup.preview_ready"));
  };

  const importBackup = async () => {
    if (preview === undefined) return;
    await commitFamilyPlanImport(preview, backupPassphrase);
    setBackupMessage(t("family.backup.imported"));
    setPreview(undefined);
    setBackupText("");
    setBackupPassphrase("");
    await refresh();
  };

  const tabs: readonly FamilyTab[] = [
    "OVERVIEW",
    "CONTACTS",
    "MEETING",
    "CHECKLISTS",
    "VAULT",
    "CARDS",
    "BACKUP",
  ];
  return (
    <div className="page-stack family-plan-page">
      <PageIntro
        body={t("page.family.body")}
        eyebrow={t("page.family.eyebrow")}
        title={t("page.family.title")}
      />
      <section className="product-draft-note" aria-labelledby="family-draft-title">
        <p className="eyebrow">{t("draft.status.DRAFT")}</p>
        <h2 id="family-draft-title">{t("family.draft.title")}</h2>
        <p>{t("family.draft.explanation")}</p>
        <dl>
          <div>
            <dt>{t("draft.scope")}</dt>
            <dd>{t("family.draft.scope")}</dd>
          </div>
          <div>
            <dt>{t("draft.source")}</dt>
            <dd>
              <a href="/learn/family-preparedness">{t("family.draft.source")}</a>
            </dd>
          </div>
          <div>
            <dt>{t("draft.researched")}</dt>
            <dd>2026-10-01</dd>
          </div>
          <div>
            <dt>{t("draft.limitations")}</dt>
            <dd>{t("family.draft.limitations")}</dd>
          </div>
        </dl>
      </section>
      <section className="family-privacy-note">
        <h2>{t("family.privacy.title")}</h2>
        <p>{t("family.privacy.body")}</p>
        <div className="family-privacy-note__facts" role="list">
          <span role="listitem">{t("family.privacy.fact.no_account")}</span>
          <span role="listitem">{t("family.privacy.fact.local")}</span>
          <span role="listitem">{t("family.privacy.fact.backup")}</span>
        </div>
        <p>
          <strong>{t("family.privacy.warning")}</strong>
        </p>
      </section>
      <nav aria-label={t("family.tabs")} className="family-tabs">
        {tabs.map((item) => (
          <button
            aria-current={tab === item ? "page" : undefined}
            key={item}
            onClick={() => setTab(item)}
          >
            {t(`family.tab.${item}`)}
          </button>
        ))}
      </nav>
      <section className="family-workspace">
        <h2 ref={sectionHeading} tabIndex={-1}>
          {t(`family.tab.${tab}`)}
        </h2>
        {quick === undefined ? (
          <div className="family-empty">
            <span className="route-state__icon">
              <Icon name="family" />
            </span>
            <div>
              <p>{t("family.empty")}</p>
              <button className="button button--primary" onClick={() => void startPlan()}>
                {locale === "th" ? "สร้างแผนของฉัน" : "Create my plan"}
              </button>
            </div>
          </div>
        ) : tab === "OVERVIEW" ? (
          <div className="family-form-grid">
            <label>
              {t("family.plan_label")}
              <input
                value={quick.plan.label}
                onChange={(event) => void updateLabel(event.currentTarget.value)}
              />
            </label>
            <dl className="family-summary">
              <div>
                <dt>{t("family.contacts")}</dt>
                <dd>{quick.contacts.length}</dd>
              </div>
              <div>
                <dt>{t("family.meeting_points")}</dt>
                <dd>{quick.meetingPoints.length}</dd>
              </div>
              <div>
                <dt>{t("family.checklists")}</dt>
                <dd>{quick.checklists.length}</dd>
              </div>
            </dl>
          </div>
        ) : tab === "CONTACTS" ? (
          <ul className="family-cards">
            {quick.contacts.map((record) => (
              <li key={record.id}>
                <label>
                  {t("family.contact_name")}
                  <input
                    value={record.name}
                    onChange={(event) =>
                      void updateContactName(record.id, event.currentTarget.value)
                    }
                  />
                </label>
                <span>{record.relationshipLabel}</span>
                <span>{record.contactValue}</span>
              </li>
            ))}
          </ul>
        ) : tab === "MEETING" ? (
          <ul className="family-cards">
            {quick.meetingPoints.map((record) => (
              <li key={record.id}>
                <label>
                  {t("family.meeting_label")}
                  <input
                    value={record.label}
                    onChange={(event) =>
                      void updateMeetingLabel(record.id, event.currentTarget.value)
                    }
                  />
                </label>
                <span>{record.locationNote}</span>
              </li>
            ))}
          </ul>
        ) : tab === "CHECKLISTS" ? (
          <div className="family-checklists">
            {quick.checklists.map((list) => (
              <fieldset key={list.id}>
                <legend>{list.title}</legend>
                {list.items.map((item) => (
                  <label key={item.id}>
                    <input
                      checked={item.completed}
                      onChange={() => void toggleChecklist(list.id, item.id)}
                      type="checkbox"
                    />
                    {item.label}
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
        ) : tab === "VAULT" ? (
          <div className="vault-panel">
            <p role="status">
              {unlocked ? t("family.vault.state_unlocked") : t("family.vault.state_locked")}
            </p>
            {!unlocked && (
              <label>
                {t("family.vault.passphrase")}
                <input
                  autoComplete="current-password"
                  type="password"
                  value={passphrase}
                  onChange={(event) => setPassphrase(event.currentTarget.value)}
                />
              </label>
            )}
            <div className="family-actions">
              {!vaultExists ? (
                <button className="button button--primary" onClick={() => void setupVault()}>
                  {t("family.vault.create")}
                </button>
              ) : !unlocked ? (
                <button className="button button--primary" onClick={() => void unlock()}>
                  {t("family.vault.unlock")}
                </button>
              ) : (
                <button className="button" onClick={lock}>
                  {t("family.vault.lock")}
                </button>
              )}
            </div>
            {vaultMessage.length > 0 && <p aria-live="polite">{vaultMessage}</p>}
            {unlocked && (
              <ul className="family-cards">
                {protectedRecords.map((record) => (
                  <li key={record.id}>
                    <strong>
                      {record.kind === "PRIVATE_NOTE" ? record.title : record.displayName}
                    </strong>
                    <span>{t(`family.vault.kind.${record.kind}`)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : tab === "CARDS" ? (
          <ReunificationCardsPanel
            protectedRecords={protectedRecords}
            quick={quick}
            vaultUnlocked={unlocked}
          />
        ) : (
          <div className="backup-panel">
            <label>
              {t("family.backup.passphrase")}
              <input
                type="password"
                value={backupPassphrase}
                onChange={(event) => setBackupPassphrase(event.currentTarget.value)}
              />
            </label>
            <button className="button" onClick={() => void exportBackup()}>
              {t("family.backup.export")}
            </button>
            <label>
              {t("family.backup.data")}
              <textarea
                rows={8}
                value={backupText}
                onChange={(event) => setBackupText(event.currentTarget.value)}
              />
            </label>
            <div className="family-actions">
              <button className="button" onClick={() => void inspectImport()}>
                {t("family.backup.preview")}
              </button>
              {preview !== undefined && (
                <button className="button button--primary" onClick={() => void importBackup()}>
                  {t("family.backup.confirm")}
                </button>
              )}
            </div>
            {preview !== undefined && (
              <dl className="family-summary">
                <div>
                  <dt>{t("family.contacts")}</dt>
                  <dd>{preview.counts.contacts}</dd>
                </div>
                <div>
                  <dt>{t("family.vault.records")}</dt>
                  <dd>{preview.counts.protectedRecords}</dd>
                </div>
              </dl>
            )}
            {backupMessage.length > 0 && <p aria-live="polite">{backupMessage}</p>}
          </div>
        )}
      </section>
      <details className="family-diagnostics">
        <summary>{t("family.diagnostics")}</summary>
        <p>{t("family.diagnostics_body")}</p>
        <dl>
          <div>
            <dt>{t("family.vault.status")}</dt>
            <dd>{unlocked ? "UNLOCKED_IN_MEMORY" : "LOCKED"}</dd>
          </div>
          <div>
            <dt>{t("family.plan_schema")}</dt>
            <dd>{quick?.plan.schemaVersion ?? "—"}</dd>
          </div>
        </dl>
      </details>
    </div>
  );
}
